// Supplier quotes
import { numOf } from '../core/budget.js';
import { useEffect, useMemo, useRef, useState } from '../core/react.js';
import { MONO, T, fmt } from '../core/theme.js';
import { Pill } from '../ui/widgets.js';
import { Icon } from '../ui/icons.js';
import { useNarrow } from '../ui/narrow.js';
import { money, numStr, suggestLine, takeDocument } from '../core/doc-upload.js';
import { DocPreview, DropCard, InvLabel, ListHead, NoProject, ReadingCard, invInput } from '../ui/doc-review.js';
import { sentenceCase } from '../core/format.js';
import { safeFileUrl } from '../core/util.js';
import { mainLines } from '../core/subbudgets.js';
import { pageHints } from '../core/page-hints.js';
import { plural } from '../core/format.js';

const pctStr = v => v == null || !isFinite(v) ? '' : String(Math.round(v * 10000) / 100);
export function Quotes({
  project,
  sections,
  fx,
  user,
  costAction,
  reloadBudget,
  flashSync,
  setView
}) {
  const subs = project?.subBudgets || [];
  const hasProject = !!project;
  const [quotes, setQuotes] = useState([]);
  const [listState, setListState] = useState('idle');
  const [listErr, setListErr] = useState('');
  const [stage, setStage] = useState('list');
  const [file, setFile] = useState(null);
  const [head, setHead] = useState(null);
  const [lines, setLines] = useState([]);
  const [note, setNote] = useState(null);
  // where the quote goes: a new sub-budget (named sbName) or an existing one; and the main line it plans against
  const [sbTarget, setSbTarget] = useState('__new');
  const [sbName, setSbName] = useState('');
  const [mainLine, setMainLine] = useState('');
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // phones have no dragging, and the panes are stacked: the add panel is above the list, not beside it
  const narrow = useNarrow();
  const fileInput = useRef(null);
  async function loadList() {
    if (!hasProject) return;
    setListState('loading');
    const res = await costAction('listQuotes', {});
    if (res.ok) {
      setQuotes(res.quotes || []);
      setListState('idle');
    } else {
      setListErr(res.error);
      setListState('error');
    }
  }
  useEffect(() => {
    loadList();
  }, [project?.id, hasProject]);
  useEffect(() => {
    const h = window.__prismHandoff;
    if (!h || !hasProject || !sections.length) return;
    window.__prismHandoff = null;
    const q = h.quote || {};
    const txt = ['From Tenders', '', 'Event: ' + (h.event || ''), 'Tender: ' + (h.rfq || ''), 'Supplier: ' + (q.supplier_name || ''), 'Currency: ' + (q.currency || ''), 'Tax: ' + Math.round((q.vat_rate || 0) * 1000) / 10 + '%', '', ...(q.line_items || []).map(l => l.description + '    ' + l.unit_price), '', 'Net total: ' + q.net_total].join('\n');
    setFile({
      name: `Tenders: ${h.rfq || 'quote'} (${q.supplier_name || 'supplier'})`,
      type: 'text/plain',
      b64: undefined,
      url: URL.createObjectURL(new Blob([txt], {
        type: 'text/plain;charset=utf-8'
      }))
    });
    populate({
      ok: true,
      quote: q
    });
  }, [hasProject, sections.length]);
  useEffect(() => () => {
    if (file?.url) URL.revokeObjectURL(file.url);
  }, [file]);
  useEffect(() => {
    const stop = e => {
      if (Array.from(e.dataTransfer?.types || []).includes('Files')) e.preventDefault();
    };
    window.addEventListener('dragover', stop);
    window.addEventListener('drop', stop);
    return () => {
      window.removeEventListener('dragover', stop);
      window.removeEventListener('drop', stop);
    };
  }, []);
  const mains = useMemo(() => mainLines(sections), [sections]);
  function discard() {
    setFile(null);
    setHead(null);
    setLines([]);
    setNote(null);
    setStage('list');
  }
  async function handleFile(f) {
    const doc = await takeDocument(f, {
      flash: flashSync,
      onStart: file => { setFile(file); setStage('reading'); setNote(null); },
      // photos are kept as the same compressed copy Claude reads, so the file is stored once
      onShrunk: patch => setFile(cur => cur && Object.assign(cur, patch))
    });
    if (!doc) return;
    const res = await costAction('extractQuote', { fileData: doc.b64, mediaType: doc.type });
    populate(res);
  }
  function populate(res) {
    const x = res.ok ? res.quote || {} : {};
    const supplier = String(x.supplier_name ?? '');
    const sug = suggestLine(sections, supplier);
    const ref = String(x.quote_number ?? '').trim();
    const vatRate = isFinite(Number(x.vat_rate)) && x.vat_rate !== null ? Number(x.vat_rate) : 0;
    setHead({
      supplier,
      number: String(x.quote_number ?? ''),
      date: String(x.quote_date ?? ''),
      valid: String(x.valid_until ?? ''),
      ccy: String(x.currency ?? '').toUpperCase(),
      vat: pctStr(vatRate),
      incVat: !!x.prices_include_vat,
      net: numStr(x.net_total),
      confidence: x.confidence || null
    });
    setSbTarget('__new');
    setSbName([supplier || 'Quote', ref].filter(Boolean).join(' · '));
    setMainLine(sug?.lineId || '');
    const raw = (Array.isArray(x.line_items) ? x.line_items : []).map((l, i) => ({
      key: 'q' + i + '_' + Date.now(),
      include: !/^optional:/i.test(String(l.description || '')),
      desc: String(l.description ?? ''),
      qty: numStr(l.quantity ?? 1),
      unit: String(l.unit ?? ''),
      up: numStr(l.unit_price ?? l.amount)
    }));
    setLines((raw.length ? raw : [{
      key: 'q0',
      include: true,
      desc: '',
      qty: '1',
      unit: '',
      up: ''
    }]).map(l => ({ ...l, target: sug?.lineId || '' })));
    if (!res.ok) setNote({
      type: 'err',
      text: `The quote couldn't be read automatically (${res.error}). Add the lines from the preview.`
    });else if (x.notes || x.confidence === 'low') setNote({
      type: 'warn',
      text: x.notes || 'Low-confidence read. Check every line against the preview.'
    });else if (!raw.length) setNote({
      type: 'warn',
      text: 'No priced lines were found. Add them from the preview.'
    });
    setStage('review');
  }
  // the quote's main line: lines that followed it follow the new one; lines pointed elsewhere stay
  function changeMainLine(id) {
    setLines(prev => prev.map(l => l.target === mainLine ? { ...l, target: id } : l));
    setMainLine(id);
  }
  const vat = head ? numOf(head.vat) / 100 : 0;
  const netOf = l => {
    const up = numOf(l.up);
    return head?.incVat && isFinite(vat) ? up / (1 + vat) : up;
  };
  const included = lines.filter(l => l.include);
  const linesNet = included.reduce((s, l) => s + (numOf(l.qty) || 0) * (netOf(l) || 0), 0);
  const quoteNet = head ? numOf(head.net) : NaN;
  const coreNet = lines.filter(l => !/^optional:/i.test(String(l.desc))).reduce((s, l) => s + (numOf(l.qty) || 0) * (netOf(l) || 0), 0);
  const netOff = isFinite(quoteNet) && Math.abs(coreNet - quoteNet) > 0.02;
  const ccyKnown = !!(head && fx[head.ccy]);
  const unassigned = included.filter(l => !l.target).length;
  function setH(k, v) {
    setHead(h => ({
      ...h,
      [k]: v
    }));
  }
  function setL(key, patch) {
    setLines(prev => prev.map(l => l.key === key ? {
      ...l,
      ...patch
    } : l));
  }
  async function apply() {
    const problems = [];
    if (!ccyKnown) problems.push(`an FX rate for ${head.ccy || 'the quote currency'} (Settings)`);
    if (!isFinite(vat) || vat < 0 || vat > 1) problems.push('a VAT rate between 0 and 100%');
    if (!included.length) problems.push('at least one ticked line');
    included.forEach(l => {
      if (!String(l.desc).trim()) problems.push('a description on every ticked line');
      if (!(numOf(l.qty) > 0) || !isFinite(numOf(l.up))) problems.push(`a quantity and price for "${l.desc || 'untitled line'}"`);
    });
    if (sbTarget === '__new' && !sbName.trim()) problems.push('a name for the new sub-budget');
    if (problems.length) {
      flashSync('Still needed: ' + [...new Set(problems)].join(', '), 'err');
      return;
    }
    const existing = subs.find(s => s.id === sbTarget);
    setSaving(true);
    const res = await costAction('applyQuote', {
      quote: {
        supplier: head.supplier.trim(),
        number: head.number.trim(),
        date: head.date,
        valid: head.valid,
        ccy: head.ccy,
        vat,
        net: isFinite(quoteNet) ? quoteNet : linesNet
      },
      subBudget: existing ? { mode: 'existing', id: existing.id, name: existing.name } : { mode: 'new', name: sbName.trim() },
      lines: included.map(l => ({
        desc: String(l.desc).trim(),
        qty: numOf(l.qty),
        unit: String(l.unit || '').trim(),
        up: Math.round(netOf(l) * 10000) / 10000,
        target: mains.has(l.target) ? l.target : ''
      })),
      fileData: file?.b64,
      mediaType: file?.type,
      addedBy: user?.name || ''
    });
    setSaving(false);
    if (!res.ok) {
      flashSync(`Not added: ${res.error}`, 'err');
      return;
    }
    flashSync(`${head.supplier || 'Quote'}: ${res.added} line${res.added === 1 ? '' : 's'} added to the sub-budget "${res.subBudget?.name || ''}"`);
    discard();
    await reloadBudget();
    loadList();
  }
  // a main budget line picker, grouped by section; '' = not planned against a line yet
  const mainSelect = (value, onChange, label, style) => React.createElement("select", {
    value: mains.has(value) ? value : '',
    onChange: e => onChange(e.target.value),
    'aria-label': label,
    style
  }, React.createElement("option", {
    value: ""
  }, "Not yet: pick on Sub-budgets"), sections.map(sec => React.createElement("optgroup", {
    key: sec.id,
    label: sentenceCase(sec.name)
  }, sec.items.filter(it => it.lineId).map(it => React.createElement("option", {
    key: it.lineId,
    value: it.lineId
  }, it.ln, ". ", it.desc)))));
  const openSub = id => { pageHints.subBudgetId = id; setView('subbudgets'); };
  if (!hasProject) return React.createElement(NoProject, { text: 'Create or pick a project to add quotes to its budget.', setView });
  if (stage === 'review' && head) {
    const confColor = {
      high: T.green,
      medium: T.amber,
      low: T.red
    }[head.confidence];
    const hField = (label, k, opts = {}) => React.createElement("div", {
      style: {
        gridColumn: opts.span ? `span ${opts.span}` : undefined
      }
    }, React.createElement(InvLabel, null, label), React.createElement("input", {
      type: opts.type || 'text',
      value: head[k],
      onChange: e => setH(k, e.target.value),
      inputMode: opts.num ? 'decimal' : undefined,
      placeholder: opts.ph || '',
      style: invInput(opts.mono || opts.num, opts.num ? {
        textAlign: 'right'
      } : {})
    }));
    const cell = {
      padding: '6px 4px',
      verticalAlign: 'top'
    };
    const tin = (mono, extra = {}) => invInput(mono, {
      padding: '7px 7px',
      fontSize: 11.5,
      ...extra
    });
    return React.createElement("div", {
      style: {
        display: 'flex',
        height: '100%',
        overflow: 'hidden'
      },
      className: "fade-up pane-row"
    }, React.createElement(DocPreview, { file, what: 'Quote' }), React.createElement("div", {
      style: {
        flex: '1 1 62%',
        minWidth: 640,
        background: T.white,
        overflowY: 'auto',
        padding: '24px 26px',
        display: 'flex',
        flexDirection: 'column',
        gap: 18
      }
    }, React.createElement("div", {
      style: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 12
      }
    }, React.createElement("div", null, React.createElement("div", {
      style: {
        fontSize: 16,
        fontWeight: 600,
        color: T.coal,
        marginBottom: 3
      }
    }, "Check the quote"), React.createElement("div", {
      style: {
        fontSize: 12,
        color: T.mist
      }
    }, "Untick anything you don't want. The lines go into a sub-budget, planned against main-budget lines; the main budget's prices don't change.")), confColor && React.createElement(Pill, {
      label: `${head.confidence} confidence`,
      color: confColor,
      bg: confColor + '18'
    })), note && React.createElement("div", {
      style: {
        padding: '10px 12px',
        fontSize: 12,
        lineHeight: 1.5,
        background: note.type === 'err' ? T.redBg : T.amberBg,
        color: note.type === 'err' ? T.red : T.amber,
        border: `1px solid ${note.type === 'err' ? T.redBr : T.amberBr}`
      }
    }, note.text), React.createElement("div", {
      style: {
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 1fr 1fr',
        gap: 12
      }
    }, hField('SUPPLIER', 'supplier'), hField('QUOTE REF', 'number', {
      mono: true
    }), hField('QUOTE DATE', 'date', {
      type: 'date'
    }), hField('VALID UNTIL', 'valid', {
      type: 'date'
    })), React.createElement("div", {
      style: {
        display: 'grid',
        gridTemplateColumns: '90px 1fr 1fr',
        gap: 12,
        alignItems: 'end'
      }
    }, React.createElement("div", null, React.createElement(InvLabel, null, "CCY"), React.createElement("select", {
      value: head.ccy,
      onChange: e => setH('ccy', e.target.value),
      style: invInput(false, {
        padding: '9px 6px'
      })
    }, !fx[head.ccy] && React.createElement("option", {
      value: head.ccy
    }, head.ccy || '—'), Object.keys(fx).map(c => React.createElement("option", {
      key: c
    }, c)))), hField('VAT %', 'vat', {
      num: true
    }), hField('QUOTE NET TOTAL', 'net', {
      num: true
    })), React.createElement("label", {
      style: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 12,
        color: T.slate,
        cursor: 'pointer',
        marginTop: -6
      }
    }, React.createElement("input", {
      type: "checkbox",
      checked: head.incVat,
      onChange: e => setH('incVat', e.target.checked)
    }), "Prices on the quote include VAT (they'll be converted to net)"), !ccyKnown && head.ccy && React.createElement("div", {
      style: {
        fontSize: 11.5,
        color: T.red,
        marginTop: -8
      }
    }, "No FX rate for ", head.ccy, ". Add it in Settings or pick the right currency."), React.createElement("div", {
      style: {
        borderTop: `1px solid ${T.rule}`,
        paddingTop: 16
      }
    }, React.createElement("div", {
      style: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 12,
        marginBottom: 14
      }
    }, React.createElement("div", null, React.createElement(InvLabel, null, "SUB-BUDGET"), React.createElement("select", {
      value: sbTarget,
      onChange: e => setSbTarget(e.target.value),
      'aria-label': 'Sub-budget',
      style: invInput(false)
    }, React.createElement("option", {
      value: "__new"
    }, "A new sub-budget\u2026"), subs.map(s => React.createElement("option", {
      key: s.id,
      value: s.id
    }, "Add to: ", s.name || 'Untitled', " (", (s.lines || []).length, " lines)")))), sbTarget === '__new' ? React.createElement("div", null, React.createElement(InvLabel, null, "NEW SUB-BUDGET NAME"), React.createElement("input", {
      value: sbName,
      onChange: e => setSbName(e.target.value),
      'aria-label': 'New sub-budget name',
      placeholder: "e.g. Ameron Cocktail Reception",
      style: invInput(false)
    })) : React.createElement("div", null), React.createElement("div", {
      style: {
        gridColumn: 'span 2'
      }
    }, React.createElement(InvLabel, null, "PLANNED AGAINST (MAIN BUDGET LINE)"), mainSelect(mainLine, changeMainLine, 'Main budget line for the quote', invInput(false)))), React.createElement("div", {
      style: {
        overflowX: 'auto'
      }
    }, React.createElement("table", {
      style: {
        width: '100%',
        minWidth: 600
      }
    }, React.createElement("thead", null, React.createElement("tr", null, ['', 'Description', 'Qty', 'Unit', 'Unit price', 'Total', 'Planned against', ''].map((h, i) => React.createElement("th", {
      key: i,
      style: {
        fontSize: 9.5,
        fontWeight: 700,
        letterSpacing: '0.08em',
        color: T.mist,
        textAlign: ['Qty', 'Unit price', 'Total'].includes(h) ? 'right' : 'left',
        padding: '0 4px 6px'
      }
    }, h.toUpperCase())))), React.createElement("tbody", null, lines.map(l => {
      const tot = (numOf(l.qty) || 0) * (netOf(l) || 0);
      return React.createElement("tr", {
        key: l.key,
        style: {
          opacity: l.include ? 1 : .45
        }
      }, React.createElement("td", {
        style: {
          ...cell,
          paddingTop: 12
        }
      }, React.createElement("input", {
        type: "checkbox",
        checked: l.include,
        onChange: e => setL(l.key, {
          include: e.target.checked
        })
      })), React.createElement("td", {
        style: {
          ...cell,
          width: '34%'
        }
      }, React.createElement("input", {
        value: l.desc,
        onChange: e => setL(l.key, {
          desc: e.target.value
        }),
        style: tin(false)
      })), React.createElement("td", {
        style: {
          ...cell,
          width: 60
        }
      }, React.createElement("input", {
        value: l.qty,
        onChange: e => setL(l.key, {
          qty: e.target.value
        }),
        inputMode: "decimal",
        style: tin(true, {
          textAlign: 'right'
        })
      })), React.createElement("td", {
        style: {
          ...cell,
          width: 60
        }
      }, React.createElement("input", {
        value: l.unit,
        onChange: e => setL(l.key, {
          unit: e.target.value
        }),
        style: tin(false)
      })), React.createElement("td", {
        style: {
          ...cell,
          width: 96
        }
      }, React.createElement("input", {
        value: l.up,
        onChange: e => setL(l.key, {
          up: e.target.value
        }),
        inputMode: "decimal",
        style: tin(true, {
          textAlign: 'right'
        })
      })), React.createElement("td", {
        style: {
          ...cell,
          ...MONO,
          fontSize: 11.5,
          textAlign: 'right',
          paddingTop: 13,
          color: T.coal,
          whiteSpace: 'nowrap'
        }
      }, isFinite(tot) ? fmt.num(tot, 2) : '—'), React.createElement("td", {
        style: {
          ...cell,
          width: '24%'
        }
      }, mainSelect(l.target, v => setL(l.key, { target: v }), 'Main budget line for ' + (l.desc || 'this line'), tin(false, {
        padding: '7px 4px'
      }))), React.createElement("td", {
        style: {
          ...cell,
          paddingTop: 10
        }
      }, React.createElement("button", {
        onClick: () => setLines(prev => prev.filter(x => x.key !== l.key)),
        title: "Remove line"
      }, React.createElement(Icon.trash, {
        s: 13,
        c: T.mist
      }))));
    })))), React.createElement("button", {
      onClick: () => setLines(prev => [...prev, {
        key: 'q' + Date.now(),
        include: true,
        desc: '',
        qty: '1',
        unit: '',
        up: '',
        target: mainLine
      }]),
      style: {
        fontSize: 11.5,
        color: T.navyL,
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        padding: '6px 0 0'
      }
    }, React.createElement(Icon.plus, {
      s: 11,
      c: T.navyL
    }), " Add a line")), React.createElement("div", {
      style: {
        background: T.ivory,
        border: `1px solid ${T.rule}`,
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6
      }
    }, React.createElement("div", {
      style: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 12,
        color: T.slate
      }
    }, React.createElement("span", null, "Ticked lines, net", head.incVat ? ' (VAT removed)' : ''), React.createElement("span", {
      style: {
        ...MONO,
        color: T.coal
      }
    }, money(linesNet, head.ccy))), netOff && React.createElement("div", {
      style: {
        fontSize: 11.5,
        color: T.amber
      }
    }, "The quote's lines (excluding optional extras) add up to ", money(coreNet), ", but its net total is ", money(quoteNet), ". Check for a missing or misread line."), unassigned > 0 && React.createElement("div", {
      style: {
        fontSize: 11.5,
        color: T.amber
      }
    }, plural(unassigned, 'line'), " not planned against a main budget line yet. You can pick one later on the Sub-budgets page."), React.createElement("div", {
      style: {
        fontSize: 11.5,
        color: T.slate
      }
    }, "Main budget lines it's planned against go from TBC to Quoted and get the supplier's name if they have none. Their prices don't change.")), React.createElement("div", {
      style: {
        display: 'flex',
        gap: 10,
        marginTop: 'auto',
        paddingTop: 6
      }
    }, React.createElement("button", {
      onClick: discard,
      disabled: saving,
      style: {
        padding: '12px 18px',
        border: `1px solid ${T.rule}`,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.1em',
        color: T.slate
      }
    }, "DISCARD"), React.createElement("button", {
      onClick: apply,
      disabled: saving,
      style: {
        flex: 1,
        padding: '12px',
        background: T.navy,
        color: T.white,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.1em',
        opacity: saving ? .6 : 1
      }
    }, saving ? 'ADDING…' : `ADD ${included.length} LINE${included.length === 1 ? '' : 'S'} TO ${sbTarget === '__new' ? 'A NEW' : 'THE'} SUB-BUDGET`))));
  }
  return React.createElement("div", {
    style: {
      display: 'flex',
      height: '100%',
      overflow: 'hidden',
      background: T.ivory
    },
    className: "fade-up pane-row",
    onDragOver: e => {
      e.preventDefault();
      if (stage === 'list') setDragOver(true);
    },
    onDragLeave: e => {
      if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
    },
    onDrop: e => {
      e.preventDefault();
      setDragOver(false);
      if (stage === 'list') handleFile(e.dataTransfer.files?.[0]);
    }
  }, React.createElement("div", {
    style: {
      width: 300,
      background: T.white,
      borderRight: `1px solid ${T.rule}`,
      padding: '26px 22px',
      display: 'flex',
      flexDirection: 'column',
      gap: 18,
      flexShrink: 0,
      overflowY: 'auto'
    }
  }, React.createElement("div", null, React.createElement("div", {
    style: {
      fontSize: 16,
      fontWeight: 600,
      color: T.coal,
      marginBottom: 4
    }
  }, "Add a supplier quote"), React.createElement("div", {
    style: {
      fontSize: 12,
      color: T.mist,
      lineHeight: 1.5
    }
  }, "Its lines and prices are read for you. You check them and they become a sub-budget, planned against the main budget's lines.")), stage === 'reading' ? React.createElement(ReadingCard, { text: 'Reading the quote', file }) : React.createElement(DropCard, { icon: Icon.quote, add: 'Add a quote', drop: 'Drop a quote here', dragOver, narrow, inputRef: fileInput, onFile: handleFile })), React.createElement("div", {
    style: {
      flex: 1,
      overflowY: 'auto',
      padding: '26px 30px'
    }
  }, React.createElement(ListHead, { title: 'Quotes', sub: project?.name, loading: listState === 'loading', onRefresh: loadList }), listState === 'error' && React.createElement("div", {
    style: {
      padding: '10px 12px',
      fontSize: 12,
      background: T.redBg,
      color: T.red,
      border: `1px solid ${T.redBr}`,
      marginBottom: 16
    }
  }, "Couldn't load quotes: ", listErr, ". Press Refresh to try again."), quotes.length === 0 && listState !== 'loading' ? React.createElement("div", {
    style: {
      border: `1px dashed ${T.ivoryDD}`,
      padding: '48px 24px',
      textAlign: 'center',
      fontSize: 12.5,
      color: T.mist
    }
  }, narrow ? "No quotes added to this budget yet. Add the first one above." : "No quotes added to this budget yet. Drop the first one on the left.") : React.createElement("table", {
    style: {
      width: '100%',
      background: T.white,
      boxShadow: '0 1px 4px rgba(62,63,67,.06)'
    }
  }, React.createElement("thead", null, React.createElement("tr", {
    style: {
      background: T.navy
    }
  }, ['Added', 'Supplier', 'Ref', 'Valid until', 'Net', 'Lines', 'Sub-budget', ''].map((h, i) => React.createElement("th", {
    key: i,
    style: {
      padding: '9px 12px',
      textAlign: h === 'Net' ? 'right' : 'left',
      fontSize: 9.5,
      fontWeight: 700,
      letterSpacing: '0.08em',
      color: 'rgba(255,255,255,.65)',
      whiteSpace: 'nowrap'
    }
  }, h)))), React.createElement("tbody", null, quotes.map((q, i) => {
    const expired = q.valid && q.valid < new Date().toLocaleDateString('en-CA');
    return React.createElement("tr", {
      key: q.id,
      style: {
        background: i % 2 === 0 ? T.white : T.ivory
      }
    }, React.createElement("td", {
      style: {
        ...MONO,
        padding: '9px 12px',
        fontSize: 11,
        color: T.mist,
        whiteSpace: 'nowrap'
      }
    }, String(q.at || '').slice(0, 10)), React.createElement("td", {
      style: {
        padding: '9px 12px',
        fontSize: 12,
        color: T.coal,
        fontWeight: 500
      }
    }, q.supplier || '—'), React.createElement("td", {
      style: {
        ...MONO,
        padding: '9px 12px',
        fontSize: 11,
        color: T.slate
      }
    }, q.number || '—'), React.createElement("td", {
      style: {
        ...MONO,
        padding: '9px 12px',
        fontSize: 11,
        color: expired ? T.red : T.slate,
        whiteSpace: 'nowrap'
      }
    }, q.valid || '—', expired ? ' · expired' : ''), React.createElement("td", {
      style: {
        ...MONO,
        padding: '9px 12px',
        fontSize: 12,
        textAlign: 'right',
        color: T.coal,
        whiteSpace: 'nowrap'
      }
    }, money(Number(q.net), q.ccy)), React.createElement("td", {
      style: {
        padding: '9px 12px',
        fontSize: 11.5,
        color: T.slate,
        whiteSpace: 'nowrap'
      }
    }, (q.subBudget != null ? plural(q.added || 0, 'line') : [q.added ? `${q.added} added` : '', q.updated ? `${q.updated} updated` : ''].filter(Boolean).join(' · ')) || '—'), React.createElement("td", {
      style: {
        padding: '9px 12px',
        fontSize: 11.5,
        color: T.slate
      }
    }, q.subBudgetId && subs.some(s => s.id === q.subBudgetId) ? React.createElement("button", {
      onClick: () => openSub(q.subBudgetId),
      style: {
        color: T.navyL,
        fontSize: 11.5,
        textDecoration: 'underline',
        textAlign: 'left'
      }
    }, subs.find(s => s.id === q.subBudgetId).name || q.subBudget) : q.subBudget || (q.section ? 'Main budget: ' + sentenceCase(q.section) : '—')), React.createElement("td", {
      style: {
        padding: '9px 12px',
        fontSize: 11,
        whiteSpace: 'nowrap'
      }
    }, safeFileUrl(q.file) ? React.createElement("a", {
      href: safeFileUrl(q.file),
      target: "_blank",
      rel: "noopener noreferrer",
      style: {
        color: T.navyL
      }
    }, "Open file") : React.createElement("span", {
      style: {
        color: T.ivoryDD
      }
    }, "\u2014")));
  })))));
}
